/**
 * utils/excelReader.js - Gestionnaire Excel générique
 * Lecture et traitement des fichiers Excel avec SheetJS
 */

import * as XLSX from "xlsx";

import logger from "./logger.js";

/**
 * Obtenir le nombre maximum de colonnes d'une feuille Excel
 * @param {Object} worksheet - Feuille Excel (SheetJS)
 * @returns {number} Nombre de colonnes (défaut: 20 si non déterminable)
 */
export function getSheetMaxCols(worksheet) {
  if (!worksheet || !worksheet["!ref"]) {
    return 20; // Valeur par défaut de sécurité
  }

  try {
    const range = worksheet["!ref"].split(":");
    const endCell = range[1] || range[0]; // Prendre la cellule de fin ou début si une seule cellule
    const colMatch = endCell.match(/[A-Z]+/); // Extraire les lettres de colonne

    if (!colMatch) return 20;

    // Convertir lettres en nombre (A=1, B=2, ..., AA=27, etc.)
    const colLetters = colMatch[0];
    let colNumber = 0;
    for (let i = 0; i < colLetters.length; i++) {
      colNumber = colNumber * 26 + (colLetters.charCodeAt(i) - "A".charCodeAt(0) + 1);
    }

    return colNumber;
  } catch (error) {
    logger.warn(
      {
        context: "excel-reader-maxcols-calculation",
        error: error.message,
        operation: "calculate-sheet-max-columns",
        hasWorksheet: !!worksheet,
        hasRef: !!worksheet?.["!ref"],
        worksheetRef: worksheet?.["!ref"] || null,
        fallbackValue: 20,
      },
      "Erreur calcul maxCols"
    );
    return 20; // Fallback sécurisé
  }
}

/**
 * Lire la valeur d'une cellule Excel de manière uniforme
 * @param {Object} cell - Cellule Excel (peut être null/undefined)
 * @returns {string} Valeur de la cellule nettoyée (string vide si vide/erreur)
 */
export function readExcelCell(cell) {
  if (!cell) {
    return "";
  }

  // Détecter les erreurs Excel (#N/A, #REF!, etc.)
  if (cell.t === "e") {
    return "";
  }

  // Lire la valeur (v = valeur, w = formatée)
  const value = cell.v !== undefined ? cell.v : cell.w || "";

  // Convertir en string et nettoyer
  return value.toString().trim();
}

/**
 * Valider un fichier Excel avant traitement
 * @param {File} file - Fichier à valider
 * @param {Object} options - Options de validation
 * @returns {Object} { isValid: boolean, errors: string[] }
 */
function _validateExcelFile(file, options = {}) {
  const errors = [];
  const defaultOptions = {
    maxSize: 50 * 1024 * 1024, // 50MB
    allowedExtensions: [".xlsx", ".xls", ".xlsm"],
    ...options,
  };

  if (!file) {
    errors.push("Aucun fichier fourni");
    return { isValid: false, errors };
  }

  // Vérifier l'extension
  const fileName = file.name.toLowerCase();
  const hasValidExtension = defaultOptions.allowedExtensions.some((ext) => fileName.endsWith(ext));

  if (!hasValidExtension) {
    errors.push(`Extension invalide. Formats acceptés: ${defaultOptions.allowedExtensions.join(", ")}`);
  }

  // Vérifier la taille
  if (file.size > defaultOptions.maxSize) {
    const maxSizeMB = Math.round(defaultOptions.maxSize / 1024 / 1024);
    errors.push(`Fichier trop volumineux (max: ${maxSizeMB}MB)`);
  }

  // Vérifier que le fichier n'est pas vide
  if (file.size === 0) {
    errors.push("Le fichier est vide");
  }

  return {
    isValid: errors.length === 0,
    errors: errors,
  };
}

/**
 * Lire et parser un fichier Excel
 * @param {File} file - Fichier Excel à lire
 * @param {Object} options - Options de lecture
 * @returns {Promise<Object>} { isValid: boolean, errors: string[], workbook: Object|null }
 */
export async function readExcelFile(file, options = {}) {
  const defaultOptions = {
    cellStyles: true,
    cellFormulas: false,
    cellDates: true,
    cellNF: false,
    sheetStubs: false,
    ...options,
  };

  // Validation du fichier
  const fileValidation = _validateExcelFile(file, options);
  if (!fileValidation.isValid) {
    return {
      isValid: false,
      errors: fileValidation.errors,
      workbook: null,
    };
  }

  try {
    // Lecture du fichier
    const arrayBuffer = await file.arrayBuffer();

    // Parsing avec SheetJS
    const workbook = XLSX.read(arrayBuffer, defaultOptions);

    if (!workbook || !workbook.SheetNames || workbook.SheetNames.length === 0) {
      return {
        isValid: false,
        errors: ["Le fichier Excel ne contient aucune feuille lisible"],
        workbook: null,
      };
    }

    return {
      isValid: true,
      errors: [],
      workbook: workbook,
    };
  } catch (error) {
    return {
      isValid: false,
      errors: [`Erreur de lecture Excel: ${error.message}`],
      workbook: null,
    };
  }
}

/**
 * Extraire les informations des feuilles
 * @param {Object} workbook - Workbook SheetJS
 * @returns {Array} Liste des feuilles avec métadonnées
 */
export function getSheetNames(workbook) {
  if (!workbook || !workbook.SheetNames) {
    return [];
  }

  return workbook.SheetNames.map((sheetName) => {
    const worksheet = workbook.Sheets[sheetName];

    if (!worksheet) {
      return {
        name: sheetName,
        rows: 0,
        cols: 0,
        isEmpty: true,
        error: "Feuille inaccessible",
      };
    }

    // Calculer les dimensions
    const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1:A1");
    const rows = Math.max(0, range.e.r - range.s.r + 1);
    const cols = Math.max(0, range.e.c - range.s.c + 1);

    return {
      name: sheetName,
      rows: rows,
      cols: cols,
      isEmpty: rows <= 1 || cols === 0,
      range: worksheet["!ref"],
      hasData: !!worksheet["!ref"],
    };
  });
}

/**
 * Extraire les colonnes d'une feuille avec échantillons
 * @param {Object} workbook - Workbook SheetJS
 * @param {string} sheetName - Nom de la feuille
 * @param {Object} options - Options d'extraction
 * @returns {Object} { isValid: boolean, errors: string[], columns: Array }
 */
export function getSheetColumns(workbook, sheetName, options = {}) {
  const defaultOptions = {
    headerRow: 1, // Ligne des en-têtes (1-based)
    sampleRows: 5, // Nombre de lignes d'échantillon
    ...options,
  };

  const errors = [];

  if (!workbook || !workbook.Sheets) {
    errors.push("Workbook invalide");
    return { isValid: false, errors, columns: [] };
  }

  const worksheet = workbook.Sheets[sheetName];
  if (!worksheet) {
    errors.push(`Feuille "${sheetName}" non trouvée`);
    return { isValid: false, errors, columns: [] };
  }

  if (!worksheet["!ref"]) {
    errors.push(`Feuille "${sheetName}" est vide`);
    return { isValid: false, errors, columns: [] };
  }

  try {
    // Convertir en JSON pour analyser
    const jsonData = XLSX.utils.sheet_to_json(worksheet, {
      header: 1, // Array de arrays
      defval: "", // Valeur par défaut pour cellules vides
      range: 0, // Depuis le début
    });

    if (jsonData.length === 0) {
      errors.push(`Feuille "${sheetName}" ne contient aucune donnée`);
      return { isValid: false, errors, columns: [] };
    }

    // Extraire les en-têtes
    const headerRowIndex = defaultOptions.headerRow - 1;
    if (headerRowIndex >= jsonData.length) {
      errors.push(`Ligne d'en-têtes ${defaultOptions.headerRow} introuvable`);
      return { isValid: false, errors, columns: [] };
    }

    const headers = jsonData[headerRowIndex] || [];
    if (headers.length === 0) {
      errors.push("Aucun en-tête de colonne trouvé");
      return { isValid: false, errors, columns: [] };
    }

    // Construire les informations des colonnes
    const columns = headers
      .map((header, colIndex) => {
        const letter = XLSX.utils.encode_col(colIndex);
        const samples = [];

        // Extraire des échantillons de données
        for (
          let row = headerRowIndex + 1;
          row < Math.min(jsonData.length, headerRowIndex + 1 + defaultOptions.sampleRows);
          row++
        ) {
          const cellValue = jsonData[row] ? jsonData[row][colIndex] : "";
          if (cellValue && cellValue.toString().trim()) {
            samples.push(cellValue.toString().trim());
          }
          if (samples.length >= 3) break; // Max 3 échantillons
        }

        return {
          index: colIndex,
          letter: letter,
          name: header ? header.toString().trim() : `Colonne ${letter}`,
          samples: samples,
          sampleText: samples.length > 0 ? samples.join(", ") + "..." : "Aucune donnée",
          isEmpty: samples.length === 0,
          hasData: samples.length > 0,
        };
      })
      .filter((col) => col.name && col.name !== ""); // Filtrer les colonnes sans nom

    return {
      isValid: true,
      errors: [],
      columns: columns,
    };
  } catch (error) {
    errors.push(`Erreur d'analyse de la feuille: ${error.message}`);
    return { isValid: false, errors, columns: [] };
  }
}

/**
 * Formater la taille de fichier en unités lisibles
 * @param {number} bytes - Taille en bytes
 * @returns {string} Taille formatée
 */
function _formatFileSize(bytes) {
  if (bytes === 0) return "0 B";
  if (typeof bytes !== "number" || bytes < 0) return "Taille inconnue";

  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

/**
 * Extraire les informations du fichier Excel
 * @param {File} file - Objet File
 * @returns {Object} Informations formatées du fichier
 */
export function extractExcelFileInfo(file) {
  return {
    name: file.name,
    size: file.size,
    sizeFormatted: _formatFileSize(file.size),
    type: file.type,
    lastModified: file.lastModified,
    lastModifiedDate: file.lastModified ? new Date(file.lastModified) : null,
    lastModifiedFormatted: file.lastModified
      ? new Intl.DateTimeFormat("fr-FR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }).format(new Date(file.lastModified))
      : "Inconnue",
  };
}

/**
 * Extraire l'aperçu de toutes les colonnes d'une feuille
 * @param {Object} workbook - Workbook SheetJS
 * @param {string} sheetName - Nom de la feuille
 * @param {Object} options - Options d'extraction
 * @returns {Array} Liste des colonnes avec aperçu
 */
export function getSheetColumnsPreview(workbook, sheetName, options = {}) {
  const defaultOptions = {
    headerRow: 2, // Ligne des titres (1-based)
    previewRows: 6, // Nombre de lignes d'aperçu
    maxColumns: 15, // Limite colonnes pour éviter surcharge
    ...options,
  };

  if (!workbook || !workbook.Sheets || !workbook.Sheets[sheetName]) {
    return [];
  }

  const worksheet = workbook.Sheets[sheetName];

  if (!worksheet["!ref"]) {
    return [];
  }

  try {
    const data = XLSX.utils.sheet_to_json(worksheet, {
      header: 1,
      range: 0,
      defval: "",
    });

    if (data.length < defaultOptions.headerRow) {
      return [];
    }

    // Headers : ligne spécifiée (index headerRow - 1)
    const headerRowIndex = defaultOptions.headerRow - 1;
    const headers = data[headerRowIndex] || [];

    // Limiter le nombre de colonnes
    const maxCols = Math.min(headers.length, defaultOptions.maxColumns);

    const columns = [];

    for (let colIndex = 0; colIndex < maxCols; colIndex++) {
      const header = headers[colIndex];
      const letter = String.fromCharCode(65 + colIndex); // A, B, C...

      // Extraire les données de preview
      const previewData = [];
      const startRow = headerRowIndex + 1;
      const endRow = Math.min(data.length, startRow + defaultOptions.previewRows);

      for (let row = startRow; row < endRow; row++) {
        const cellValue = data[row] ? data[row][colIndex] : "";
        if (cellValue !== null && cellValue !== undefined && cellValue !== "") {
          previewData.push(String(cellValue).substring(0, 20));
        }
      }

      columns.push({
        index: colIndex,
        letter: letter,
        name: header ? String(header).substring(0, 30) : `Colonne ${letter}`,
        preview: previewData,
        previewText: previewData.length > 0 ? previewData.slice(0, 3).join(", ") + "..." : "Aucune donnée",
        hasData: previewData.length > 0,
        totalPreviewRows: previewData.length,
      });
    }

    return columns;
  } catch (error) {
    logger.error(
      {
        context: "excel-reader-columns-preview",
        error: error.message,
        operation: "extract-sheet-columns-preview",
        sheetName,
        hasWorkbook: !!workbook,
        hasWorksheet: !!workbook?.Sheets?.[sheetName],
        hasRef: !!workbook?.Sheets?.[sheetName]?.["!ref"],
        options: defaultOptions,
      },
      "Erreur extraction colonnes preview"
    );
    return [];
  }
}
